import type { IAugmentedJQuery, IComponentController, ILogService, IScope } from "angular";
import "@progress/kendo-ui/js/kendo.data";
import "@progress/kendo-ui/js/kendo.grid";
import "angular-agility";
import "angular-translate";

import type { IResponse, ITranslationKey } from "App/Modules/Models";
import { TranslationsFunctions } from "App/Modules/Models";

import { values } from "lodash";
import { FORMAT_TYPES } from "App/Modules/Models/Models/TranslationsFunctions";
import type { ITranslationKeysRepository } from "App/Modules/Repositories";

export default class TranslationKeysComponentController implements IComponentController {
    public static $inject = [
        "$scope",
        "$log",
        "$translate",
        "aaNotify",
        "mmTranslationKeysRepository",
        "irLanguageCacheService",
    ];

    public translationKeys: ITranslationKey[];

    public translationKeysFilterText: string;

    public gridColumns: kendo.ui.GridColumn[];

    public translationDataSource: kendo.data.DataSource;

    public initialLoadingError: IResponse<any>;

    public lastError: IResponse<any>;

    public pendingSetupRequests = 1;

    constructor(
        private readonly $scope: IScope,
        private readonly $log: ILogService,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly aaNotify: any,
        private readonly translationKeysRepository: ITranslationKeysRepository,
        private readonly languageCacheService: { clearCache: () => void },
    ) {}

    public $onInit() {
        this.$log.debug("$onInit TKCC");
        this.translationDataSource = new kendo.data.DataSource({
            autoSync: true,
            batch: false,
            pageSize: 25,
            schema: {
                model: {
                    fields: {
                        defaultTranslation: {
                            editable: true,
                            validation: {
                                compileMessageFormatValidation: (input: IAugmentedJQuery) => {
                                    return TranslationsFunctions.compileMessage(input, "defaultTranslation");
                                },
                                messageFormatValidation: (input: IAugmentedJQuery) => {
                                    return TranslationsFunctions.checkMessageFormat(input, "defaultTranslation");
                                },
                                required: true,
                            },
                        },
                        description: { editable: true },
                        format: {
                            defaultValue: FORMAT_TYPES.plain.value,
                            editable: true,
                            validation: { required: true },
                        },
                        keyName: {
                            editable: true,
                            validation: { required: true },
                        },
                        keyPrefix: {
                            editable: true,
                            validation: { required: true },
                            defaultValue: "Entities.",
                        },
                        stringKey: { editable: false },
                        variables: { editable: true },
                    },
                    id: "stringKey",
                },
            },
            transport: {
                create: (e: kendo.data.DataSourceTransportOptions) => {
                    if (!e.data.keyPrefix || !e.data.keyName || !e.data.defaultTranslation) {
                        e.success({});
                        return;
                    }

                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    e.data.stringKey = e.data.keyPrefix + e.data.keyName;
                    this.translationKeysRepository.post(e.data).subscribe(
                        (translation) => {
                            e.success(translation.data);
                            this.$translate("Entities.TranslationKey.AddedTranslationKey", {
                                key: translation.data.stringKey,
                            }).then((message) => this.aaNotify.success(message));
                            this.languageCacheService.clearCache();
                        },
                        (error) => {
                            this.lastError = error;
                        },
                    );
                },
                destroy: (e: kendo.data.DataSourceTransportOptions) => {
                    this.translationKeysRepository.delete(e.data).subscribe(
                        () => {
                            e.success();
                            this.$translate("Entities.TranslationKey.DeletedTranslationKey", {
                                key: e.data.stringKey,
                            }).then((message) => this.aaNotify.success(message));
                            this.languageCacheService.clearCache();
                        },
                        (error) => {
                            this.lastError = error;
                        },
                    );
                },
                read: (e: kendo.data.DataSourceTransportOptions) => {
                    this.translationKeysRepository.getTranslationKeys().subscribe(
                        (translationKeys) => {
                            if (translationKeys.data) {
                                e.success(translationKeys.data);
                            }
                        },
                        (error) => {
                            this.initialLoadingError = error;
                            this.lastError = error;
                        },
                    );
                },
                update: (e: kendo.data.DataSourceTransportOptions) => {
                    this.translationKeysRepository.patch(e.data).subscribe(
                        (translationKey) => {
                            e.success(translationKey.data);
                            this.$translate("Entities.TranslationKey.UpdatedTranslationKey", {
                                key: translationKey.data.stringKey,
                            }).then((message) => this.aaNotify.success(message));

                            this.languageCacheService.clearCache();
                        },
                        (error) => {
                            this.lastError = error;
                        },
                    );
                },
            } as any,
        });

        this.gridColumns = [
            {
                field: "keyPrefix",
                title: "Category",
                values: this.translationKeysRepository.getTranslationKeyPrefixes(),
                width: 160,
            },
            { field: "keyName", title: "SubCategory & Key (SubCategory.Key)" },
            {
                field: "defaultTranslation",
                title: "Default translation (English)",
                template:
                    "<pre ng-non-bindable style='font: inherit; white-space: pre-wrap;'>#:defaultTranslation#</pre>",
                editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                    $(
                        `<textarea style="width: 100%; height: 100%; margin: -40px -10px;" data-bind="value: ${options.field}"></textarea>`,
                    ).appendTo(container);
                },
            },
            {
                field: "format",
                title: "Format",
                values: values(FORMAT_TYPES),
                width: 70,
            },
            { field: "variables", title: "Variables", width: 200 },
            { field: "description", title: "Description", width: 140 },
            {
                command: [
                    {
                        name: "destroy",
                        template:
                            '<a class="k-button k-grid-delete k-button-icon " href="" ' +
                            'style="min-width:16px;"><span class="fal fa-trash-alt"></span></a>',
                    },
                ],
                width: "40px",
            },
        ];
        this.$log.debug("Done $onInit TKCC");
    }

    public resetTranslationKeysFilter() {
        this.translationKeysFilterText = null;

        this.translationDataSource.filter({
            filters: [],
            logic: "or",
        });
    }

    public translationKeysFilterUpdated() {
        this.translationDataSource.filter({
            filters: [
                {
                    field: "keyPrefix",
                    operator: "contains",
                    value: this.translationKeysFilterText,
                },
                {
                    field: "keyName",
                    operator: "contains",
                    value: this.translationKeysFilterText,
                },
                {
                    field: "defaultTranslation",
                    operator: "contains",
                    value: this.translationKeysFilterText,
                },
                {
                    field: "keyPrefix",
                    operator: "eq",
                    value: "",
                },
                {
                    field: "keyName",
                    operator: "eq",
                    value: "",
                },
                {
                    field: "defaultTranslation",
                    operator: "eq",
                    value: "",
                },
            ],
            logic: "or",
        });
    }
}
